import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Form, Row } from 'react-bootstrap';
import CommonButtons from './commonButtons';
import NoteDropdown from './noteDropdown';

const NeurologyExamination = ({
  patientInfo,
  setPatientInfo,
  selectedOnExamination,
  handleOnExaminationData,
}) => {
  let { nervousSystem } = selectedOnExamination;

  const [fieldName, setFieldName] = useState('');
  const [isBtn, setIsBtn] = useState(false);
  const [btnInputArray, setBtnInputArray] = useState([
    3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
  ]);

  const allNotes = useSelector((state) => state?.prescriptionReducer?.prescriptionInfo?.itemElementNotes || []);

  let defaultData = {
    gcs: { gcs: '', note: '' },
    consciousness: { status: '', note: '' },
    orientation: { status: '', type: '', note: '' },
    delusion: { status: '', note: '' },
    hallucination: { status: '', type: '', note: '' },
    memory: { status: '', type: '', note: '' },
    mmse: { value: '', note: '' },
    speech: { status: '', type: '', note: '' },
    cranialNerves: { status: '', type: '', note: '' },
    wasting: { status: '', type: '', note: '' },
    power: { value: '', type: '', note: '' },
    jerks: {
      biceps: { checked: false, status: '', note: '' },
      triceps: { checked: false, status: '', note: '' },
      supinator: { checked: false, status: '', note: '' },
      knee: { checked: false, status: '', note: '' },
      ankle: { checked: false, status: '', note: '' },
      jaw: { checked: false, status: '', note: '' },
    },

    plantorResponse: {
      glabellar: { checked: false, status: '', note: '' },
      planterRt: { checked: false, status: '', note: '' },
      planterLt: { checked: false, status: '', note: '' },
    },
    cerebellarFunction: { status: '', type: '', note: '' },
    sensorySystem: { status: '', type: '', note: '' },
    slrTest: { rt: '', lt: '' },
    gaitFunction: { status: '', type: '', note: '' },

    neckRigidity: { status: '', note: '' },
    kernigSign: { status: '', note: '' },
    brudzinskiSign: { status: '', note: '' },

    jvp: { status: '', note: '' },
    pulse: { status: '', note: '' },
    murmur: { status: '', note: '' },

    ascitis: { status: '', note: '' },
    hepatomegaly: { status: '', note: '' },
    splenomegaly: { status: '', note: '' },

    rr: { status: '', note: '' },
    rhanchi: { status: '', note: '' },
    creps: { status: '', note: '' },
    breathSound: { status: '', note: '' },
    gain: {},
  };

  const powerTypes = [' Rt UL', ' Lt UL', ' Rt LL', ' Lt LL'];

  let selectedData = nervousSystem || defaultData;

  const handleChange = (field, value, colName, gcsVal) => {
    const updatedData = { ...selectedData };

    // Handle special case for GCS
    if (field === 'gcs') {
      updatedData.gcs.value = gcsVal + '/15';
      setIsBtn(false);
      handleOnExaminationData(updatedData, 'nervousSystem');
      return;
    }

    if (field === 'mmseValue') {
      updatedData.mmse.value = value + '/30';
      setIsBtn(false);
      handleOnExaminationData(updatedData, 'nervousSystem');
      return;
    }

    if (field === 'powerValue') {
      updatedData.power.value = value + '/5';
      setIsBtn(false);
      handleOnExaminationData(updatedData, 'nervousSystem');
      return;
    }

    // Extract the base field name and property type
    const [baseField, propertyType = 'status'] =
      field
        .replace(/Status|Type|Note|Value/g, '')
        .match(/([a-z]+)(Status|Type|Note|Value)?$/i) || [];
    const camelCaseField =
      baseField.charAt(0).toLowerCase() + baseField.slice(1);

    if (!updatedData[camelCaseField]) {
      updatedData[camelCaseField] = {}; // Ensure the object exists
    }

    if (camelCaseField === 'power' && field === 'value') {
      updatedData.power.value = value !== '' ? Number(value) : ''; // Convert to number
    } else if (camelCaseField === 'power' && field === 'powerType') {
      // Toggle checkboxes for power.type
      const currentTypes = updatedData.power?.type || [];
      const newTypes = currentTypes.includes(value)
        ? currentTypes.filter((t) => t !== value) // Remove if already selected
        : [...currentTypes, value]; // Add if not selected

      updatedData.power.type = newTypes;
    } else if (
      field === 'jerksChecked' ||
      field === 'jerksStatus' ||
      field === 'jerksNote'
    ) {
      const currentItem = (updatedData.jerks && updatedData.jerks[colName]) || {
        checked: false,
        status: '',
        note: '',
      };
      let newItem;
      if (field === 'jerksChecked') {
        const nowChecked = !currentItem.checked;
        newItem = {
          ...currentItem,
          checked: nowChecked,
          status: nowChecked ? currentItem.status : '',
          note: nowChecked ? currentItem.note : '',
        };
      } else {
        newItem = {
          ...currentItem,
          [field === 'jerksStatus' ? 'status' : 'note']: value,
        };
      }
      updatedData.jerks = { ...(updatedData.jerks || {}), [colName]: newItem };
    } else if (
      field === 'reflexChecked' ||
      field === 'reflexStatus' ||
      field === 'reflexNote'
    ) {
      const currentItem = (updatedData.plantorResponse &&
        updatedData.plantorResponse[colName]) || {
        checked: false,
        status: '',
        note: '',
      };
      let newItem;
      if (field === 'reflexChecked') {
        const nowChecked = !currentItem.checked;
        newItem = {
          ...currentItem,
          checked: nowChecked,
          status: nowChecked ? currentItem.status : '',
          note: nowChecked ? currentItem.note : '',
        };
        const clearedOthers = {};
        Object.keys(updatedData.plantorResponse || {}).forEach((k) => {
          if (k !== colName)
            clearedOthers[k] = { checked: false, status: '', note: '' };
        });
        updatedData.plantorResponse = {
          ...(updatedData.plantorResponse || {}),
          ...clearedOthers,
          [colName]: newItem,
        };
      } else {
        newItem = {
          ...currentItem,
          [field === 'reflexStatus' ? 'status' : 'note']: value,
        };
        updatedData.plantorResponse = {
          ...(updatedData.plantorResponse || {}),
          [colName]: newItem,
        };
      }
    } else {
      const propertyMap = {
        Status: 'status',
        Type: 'type',
        Note: 'note',
        Value: 'value',
      };

      const propertyToUpdate = field.match(/(Status|Type|Note|Value)$/)?.[0];
      const property = propertyMap[propertyToUpdate] || 'status';
      updatedData[camelCaseField][property] = value;
    }

    setIsBtn(false);
    handleOnExaminationData(updatedData, 'nervousSystem');
    return updatedData;
  };

  const getValueFromFraction = (fractionString) => {
    if (!fractionString) return null;
    const values = fractionString.split('/');
    if (values[0]) return values[0];
    else return null;
  };

  return (
    <>
      <Row className="title">
        <Col>Higher Cerebral Function</Col>
      </Row>
      <hr />
      <Row>
        <Col md={12}>
          {/* Glasgow Coma Scale */}
          <Row className="mb-3 align-items-flex-start general-examination">
            <Col md={3} className="label-text">
              GCS:
            </Col>
            <Col md={5}>
              <Form.Control
                className="form-control-sm"
                type="number"
                placeholder="Enter GCS"
                value={getValueFromFraction(nervousSystem?.gcs?.value) || ''} // Updated to use correct path
                onClick={() => setIsBtn(isBtn ? false : true)}
                style={{ width: '80%', display: 'inline-block' }}
                onChange={(e) =>
                  handleChange('gcs', null, null, e.target.value)
                }
              />{' '}
              <span
                style={{
                  fontSize: '1.1rem',
                  marginLeft: '.2rem',
                }}
              >
                / 15
              </span>
              {isBtn && (
                <CommonButtons
                  unitArray={btnInputArray}
                  clickAction={handleChange}
                  rowName={'gcs'}
                />
              )}
            </Col>
            <Col md={4}>
              <NoteDropdown
                value={nervousSystem?.gcs?.note || ''}
                onChange={(val) => handleChange('gcsNote', val)}
                placeholder="Add note"
                suggestions={allNotes.filter(
                  (n) => n.elementType === 'nervousSystem' && n.itemElement === 'gcs',
                )}
              />
            </Col>
          </Row>

          {/* Level of Consciousness */}
          <Row className="mb-3 align-items-flex-start">
            <Col md={3} className="label-text">
              Level of Consciousness:
            </Col>
            <Col md={5}>
              <Row className="align-items-flex-start">
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    id="conscious"
                    label="Conscious"
                    name="consciousnessLevel"
                    checked={
                      nervousSystem?.consciousness?.status === 'conscious'
                    }
                    onClick={() =>
                      handleChange(
                        'consciousnessStatus',
                        nervousSystem?.consciousness?.status === 'conscious'
                          ? ''
                          : 'conscious',
                      )
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    id="confused"
                    label="Confused"
                    name="consciousnessLevel"
                    checked={
                      nervousSystem?.consciousness?.status === 'confused'
                    }
                    onClick={() =>
                      handleChange(
                        'consciousnessStatus',
                        nervousSystem?.consciousness?.status === 'confused'
                          ? ''
                          : 'confused',
                      )
                    }
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    id="unConscious"
                    label="Unconscious"
                    name="consciousnessLevel"
                    checked={
                      nervousSystem?.consciousness?.status === 'unConscious'
                    }
                    onClick={() =>
                      handleChange(
                        'consciousnessStatus',
                        nervousSystem?.consciousness?.status === 'unConscious'
                          ? ''
                          : 'unConscious',
                      )
                    }
                  />
                </Col>
                {/* <Col md={6}>
                  <Form.Check
                    type="radio"
                    id="coma"
                    label="Coma"
                    name="consciousnessLevel"
                    checked={nervousSystem?.consciousness?.status === 'coma'}
                    onClick={() =>
                      handleChange(
                        'consciousnessStatus',
                        nervousSystem?.consciousness?.status === 'coma'
                          ? ''
                          : 'coma',
                      )
                    }
                  />
                </Col> */}
              </Row>
            </Col>
            <Col md={4}>
              <NoteDropdown
                value={nervousSystem?.consciousness?.note || ''}
                onChange={(val) => handleChange('consciousnessNote', val)}
                placeholder="Add note"
                suggestions={allNotes.filter(
                  (n) => n.elementType === 'nervousSystem' && n.itemElement === 'consciousness',
                )}
              />
            </Col>
          </Row>

          {/* Orientation */}
          <Row className="mb-3 align-items-flex-start">
            <Col md={3} className="label-text">
              Orientation:
            </Col>
            <Col md={5}>
              <Row className="align-items-flex-start">
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    label="Oriented"
                    name="orientationStatus"
                    id="orientation_oriented"
                    checked={nervousSystem?.orientation?.status === 'oriented'}
                    onClick={() =>
                      handleChange(
                        'orientationStatus',
                        nervousSystem?.orientation?.status === 'oriented'
                          ? ''
                          : 'oriented',
                      )
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    label="Disoriented"
                    name="orientationStatus"
                    id="orientation_disoriented"
                    checked={
                      nervousSystem?.orientation?.status === 'disoriented'
                    }
                    onClick={() =>
                      handleChange(
                        'orientationStatus',
                        nervousSystem?.orientation?.status === 'disoriented'
                          ? ''
                          : 'disoriented',
                      )
                    }
                  />
                </Col>
              </Row>
            </Col>
            <Col
              md={4}
              className={`d-flex align-items-flex-start ${
                nervousSystem?.orientation?.status === 'disoriented'
                  ? 'gap-2'
                  : ''
              }`}
            >
              {nervousSystem?.orientation?.status === 'disoriented' && (
                <div className="me-2" style={{ flex: '1' }}>
                  <Form.Select
                    size="sm"
                    className="form-control-sm"
                    style={{ width: '90%' }}
                    value={nervousSystem?.orientation?.type || ''}
                    onChange={(e) =>
                      handleChange('orientationType', e.target.value)
                    }
                  >
                    <option value="">Select...</option>
                    <option value="time">Time</option>
                    <option value="place">Place</option>
                    <option value="person">Person</option>
                  </Form.Select>
                </div>
              )}
              <div
                style={{
                  flex:
                    nervousSystem?.orientation?.status === 'disoriented'
                      ? '1'
                      : '1',
                }}
              >
                <NoteDropdown
                  value={nervousSystem?.orientation?.note || ''}
                  onChange={(val) => handleChange('orientationNote', val)}
                  placeholder="Add note"
                  suggestions={allNotes.filter(
                    (n) => n.elementType === 'nervousSystem' && n.itemElement === 'orientation',
                  )}
                />
              </div>
            </Col>
          </Row>

          {/* Memory */}
          <Row className="mb-3 align-items-flex-start">
            <Col md={3} className="label-text">
              Memory:
            </Col>
            <Col md={5}>
              <Row className="align-items-flex-start">
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    label="Intact"
                    name="memory"
                    id="memory_intact"
                    checked={nervousSystem?.memory?.status === 'intact'}
                    onClick={() =>
                      handleChange(
                        'memoryStatus',
                        nervousSystem?.memory?.status === 'intact'
                          ? ''
                          : 'intact',
                      )
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    label="Impaired"
                    name="memory"
                    id="memory_impaired"
                    checked={nervousSystem?.memory?.status === 'impaired'}
                    onClick={() =>
                      handleChange(
                        'memoryStatus',
                        nervousSystem?.memory?.status === 'impaired'
                          ? ''
                          : 'impaired',
                      )
                    }
                  />
                </Col>
              </Row>
            </Col>
            <Col
              md={4}
              className={`d-flex align-items-flex-start ${
                nervousSystem?.memory?.status === 'impaired' ? 'gap-2' : ''
              }`}
            >
              {nervousSystem?.memory?.status === 'impaired' && (
                <div className="me-2" style={{ flex: '1' }}>
                  <Form.Select
                    size="sm"
                    className="form-control-sm"
                    value={nervousSystem?.memory?.type || ''}
                    onChange={(e) => handleChange('memoryType', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="immediate">Immediate</option>
                    <option value="short_term">Short Term</option>
                    <option value="long_term">Long Term</option>
                  </Form.Select>
                </div>
              )}
              <div
                style={{
                  flex:
                    nervousSystem?.memory?.status === 'impaired' ? '1' : '1',
                }}
              >
                <NoteDropdown
                  value={nervousSystem?.memory?.note || ''}
                  onChange={(val) => handleChange('memoryNote', val)}
                  placeholder="Add note"
                  suggestions={allNotes.filter(
                    (n) => n.elementType === 'nervousSystem' && n.itemElement === 'memory',
                  )}
                />
              </div>
            </Col>
          </Row>

          {/* Delusion/Illusion */}
          <Row className="mb-3 align-items-center">
            <Col md={3} className="label-text">
              Delusion:
            </Col>
            <Col md={5}>
              <Row>
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    id="present"
                    name="delusionLevel"
                    label="Present"
                    checked={nervousSystem?.delusion?.status === 'present'}
                    onClick={() =>
                      handleChange(
                        'delusionStatus',
                        nervousSystem?.delusion?.status === 'present'
                          ? ''
                          : 'present',
                      )
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    id="absent"
                    name="delusionLevel"
                    label="Absent"
                    checked={nervousSystem?.delusion?.status === 'absent'}
                    onClick={() =>
                      handleChange(
                        'delusionStatus',
                        nervousSystem?.delusion?.status === 'absent'
                          ? ''
                          : 'absent',
                      )
                    }
                  />
                </Col>
              </Row>
            </Col>
            <Col md={4}>
              <NoteDropdown
                value={nervousSystem?.delusion?.note || ''}
                onChange={(val) => handleChange('delusionNote', val)}
                placeholder="Add note"
                suggestions={allNotes.filter(
                  (n) => n.elementType === 'nervousSystem' && n.itemElement === 'delusion',
                )}
              />
            </Col>
          </Row>

          {/* Hallucination */}
          <Row className="mb-3 align-items-center">
            <Col md={3} className="label-text">
              Hallucination:
            </Col>
            <Col md={5}>
              <Row className="align-items-flex-start">
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    id="hallucination_present"
                    label="Present"
                    name="hallucinationStatus"
                    checked={nervousSystem?.hallucination?.status === 'present'}
                    onClick={() =>
                      handleChange(
                        'hallucinationStatus',
                        nervousSystem?.hallucination?.status === 'present'
                          ? ''
                          : 'present',
                      )
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="radio"
                    id="hallucination_absent"
                    label="Absent"
                    name="hallucinationStatus"
                    checked={nervousSystem?.hallucination?.status === 'absent'}
                    onClick={() =>
                      handleChange(
                        'hallucinationStatus',
                        nervousSystem?.hallucination?.status === 'absent'
                          ? ''
                          : 'absent',
                      )
                    }
                  />
                </Col>
              </Row>
            </Col>

            <Col
              md={4}
              className={`d-flex align-items-center ${
                nervousSystem?.hallucination?.status === 'present'
                  ? 'gap-2'
                  : ''
              }`}
            >
              {nervousSystem?.hallucination?.status === 'present' && (
                <div className="me-2" style={{ flex: '1' }}>
                  <Form.Select
                    size="sm"
                    className="form-control-sm"
                    value={nervousSystem?.hallucination?.type || ''}
                    onChange={(e) =>
                      handleChange('hallucinationType', e.target.value)
                    }
                  >
                    <option value="">Select...</option>
                    <option value="auditory">Auditory</option>
                    <option value="visual">Visual</option>
                    <option value="tactile">Tactile</option>
                    <option value="gustatory">Gustatory</option>
                  </Form.Select>
                </div>
              )}
              <div
                style={{
                  flex:
                    nervousSystem?.hallucination?.status === 'present'
                      ? '1'
                      : '1',
                }}
              >
                <NoteDropdown
                  value={nervousSystem?.hallucination?.note || ''}
                  onChange={(val) => handleChange('hallucinationNote', val)}
                  placeholder="Add note"
                  suggestions={allNotes.filter(
                    (n) => n.elementType === 'nervousSystem' && n.itemElement === 'hallucination',
                  )}
                />
              </div>
            </Col>
          </Row>

          {/* MMSE */}
          <Row className="mb-3 align-items-center">
            <Col md={3} className="label-text">
              MMSE:
            </Col>
            <Col md={5}>
              <Form.Control
                size="sm"
                type="number"
                placeholder="Enter MMSE"
                value={getValueFromFraction(nervousSystem?.mmse?.value) || ''}
                onChange={(e) => handleChange('mmseValue', e.target.value)}
                style={{ display: 'inline-block', width: '80%' }}
              />
              <span
                style={{
                  fontSize: '1.1rem',
                  marginLeft: '.25rem',
                }}
              >
                / 30
              </span>
            </Col>
            <Col md={4}>
              <NoteDropdown
                value={nervousSystem?.mmse?.note || ''}
                onChange={(val) => handleChange('mmseNote', val)}
                placeholder="Add note"
                suggestions={allNotes.filter(
                  (n) => n.elementType === 'nervousSystem' && n.itemElement === 'mmse',
                )}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      {/* Speech */}
      {/* <Row className="title mt-4">
        <Col>Speech</Col>
      </Row>
      <hr /> */}
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Speech:
        </Col>

        {/* Radio Buttons */}
        <Col md={5}>
          <Row className="mb-2">
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Normal"
                name="speech"
                id="speech_normal"
                checked={nervousSystem?.speech?.status === 'normal'}
                onClick={() => handleChange('speechStatus', 'normal')}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Aphasia"
                name="speech"
                id="speech_aphasia"
                checked={nervousSystem?.speech?.status === 'aphasia'}
                onClick={() => handleChange('speechStatus', 'aphasia')}
              />
            </Col>
          </Row>

          <Row className="mb-2">
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Dysphonia"
                name="speech"
                id="speech_dysphonia"
                checked={nervousSystem?.speech?.status === 'dysphonia'}
                onClick={() => handleChange('speechStatus', 'dysphonia')}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Dysarthria"
                name="speech"
                id="speech_dysarthria"
                checked={nervousSystem?.speech?.status === 'dysarthria'}
                onClick={() => handleChange('speechStatus', 'dysarthria')}
              />
            </Col>
          </Row>
          {/* <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Dysphasia/Aphasia"
                name="speech"
                id="speech_dysphasia"
                checked={nervousSystem?.speech?.status === 'dysphasia/aphasia'}
                onClick={() =>
                  handleChange('speechStatus', 'dysphasia/aphasia')
                }
              />
            </Col>
          </Row> */}
        </Col>

        {/* Dropdown & Note */}
        <Col md={4}>
          {/* Dropdown for Dysarthria or Dysphonia */}
          {['dysarthria', 'dysphonia'].includes(
            nervousSystem?.speech?.status,
          ) && (
            <Form.Select
              size="sm"
              className="form-control-sm mb-2"
              style={{ width: '100%' }}
              value={nervousSystem?.speech?.type || ''}
              onChange={(e) => handleChange('speechType', e.target.value)}
            >
              <option value="">Select...</option>
              <option value="slurring speech">Slurring speech</option>
              <option value="slurred speech">Slurred Speech</option>
              <option value="hot potato/mumbling speech">
                Hot potato/Mumbling Speech
              </option>
              <option value="nasal intonation">Nasal intonation</option>
              <option value="monotonous">Monotonous</option>
            </Form.Select>
          )}

          {/* Dropdown for Dysphasia/Aphasia */}
          {nervousSystem?.speech?.status === 'dysphasia/aphasia' && (
            <Form.Select
              size="sm"
              className="form-control-sm mb-2"
              style={{ width: '100%' }}
              value={nervousSystem?.speech?.type || ''}
              onChange={(e) => handleChange('speechType', e.target.value)}
            >
              <option value="">Select...</option>
              <option value="motor">Motor</option>
              <option value="sensory">Sensory</option>
              <option value="conduction">Conduction</option>
              <option value="trascahied">Trascahied</option>
              <option value="global">Global</option>
            </Form.Select>
          )}

          {/* Note Input */}
          <NoteDropdown
            value={nervousSystem?.speech?.note || ''}
            onChange={(val) => handleChange('speechNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'speech',
            )}
          />
        </Col>
      </Row>
      {/** Sense of Time, place and position*/}
      {/* <Row className="title mt-4">
        <Col>Sense of Time, place and position</Col>
      </Row>
      <hr /> */}
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Sense of Time, place and position:
        </Col>

        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Intact"
                name="senseOfTimePlacePosition"
                id="senseOfTimePlacePosition_intact"
                checked={
                  nervousSystem?.senseOfTimePlacePosition?.status === 'intact'
                }
                onClick={() =>
                  handleChange(
                    'senseOfTimePlacePositionStatus',
                    nervousSystem?.senseOfTimePlacePosition?.status === 'intact'
                      ? ''
                      : 'intact',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Impaired"
                name="senseOfTimePlacePosition"
                id="senseOfTimePlacePosition_impaired"
                checked={
                  nervousSystem?.senseOfTimePlacePosition?.status === 'impaired'
                }
                onClick={() =>
                  handleChange(
                    'senseOfTimePlacePositionStatus',
                    nervousSystem?.senseOfTimePlacePosition?.status ===
                      'impaired'
                      ? ''
                      : 'impaired',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="title mt-3">
        <Col>Cranial Nerves</Col>
      </Row>
      <hr />
      {/* Cranial Nerves */}
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Cranial Nerves:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Intact"
                name="cranialNerves"
                id="cranialNerves_intact"
                checked={nervousSystem?.cranialNerves?.status === 'intact'}
                onClick={() =>
                  handleChange(
                    'cranialNervesStatus',
                    nervousSystem?.cranialNerves?.status === 'intact'
                      ? ''
                      : 'intact',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Palsy"
                name="cranialNerves"
                id="cranialNerves_palsy"
                checked={nervousSystem?.cranialNerves?.status === 'palsy'}
                onClick={() =>
                  handleChange(
                    'cranialNervesStatus',
                    nervousSystem?.cranialNerves?.status === 'palsy'
                      ? ''
                      : 'palsy',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          {nervousSystem?.cranialNerves?.status === 'palsy' && (
            <Form.Select
              size="sm"
              className="form-control-sm mb-2"
              style={{ width: '100%' }}
              value={nervousSystem?.cranialNerves?.type || ''}
              onChange={(e) =>
                handleChange('cranialNervesType', e.target.value)
              }
            >
              <option value="">Select...</option>
              <option value="cni">CN I</option>
              <option value="cnii">CN II</option>
              <option value="cniii">CN III</option>
              <option value="cniv">CN IV</option>
              <option value="cnv">CN V</option>
              <option value="cnvi">CN VI</option>
              <option value="cnvii">CN VII</option>
              <option value="cnviii">CN VIII</option>
              <option value="cnix">CN IX</option>
              <option value="cnx">CN X</option>
              <option value="cnxi">CN XI</option>
              <option value="cnxii">CN XII</option>
              <option value="cnxiii">CN XIII</option>
              <option value="cnxiv">CN XIV</option>
              <option value="cnxv">CN XV</option>
              <option value="cnxvi">CN XVI</option>
              <option value="cnxvii">CN XVII</option>
              <option value="cnxviii">CN XVIII</option>
              <option value="cnxix">CN XIX</option>
              <option value="cnxx">CN XX</option>
              <option value="cnxxi">CN XXI</option>
              <option value="cnxxii">CN XXII</option>
            </Form.Select>
          )}
          <NoteDropdown
            value={nervousSystem?.cranialNerves?.note || ''}
            onChange={(val) => handleChange('cranialNervesNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'cranialNerves',
            )}
          />
        </Col>
      </Row>
      <Row className="title mt-3">
        <Col>Motor Function</Col>
      </Row>
      <hr />
      {/* Wasting */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          Muscle Wasting:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Present"
                name="wasting"
                id="wasting_present"
                checked={nervousSystem?.wasting?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'wastingStatus',
                    nervousSystem?.wasting?.status === 'present'
                      ? ''
                      : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                label="Absent"
                name="wasting"
                id="wasting_absent"
                checked={nervousSystem?.wasting?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'wastingStatus',
                    nervousSystem?.wasting?.status === 'absent' ? '' : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col
          md={4}
          className={`d-flex align-items-center ${
            nervousSystem?.wasting?.status === 'present' ? 'gap-2' : ''
          }`}
        >
          {nervousSystem?.wasting?.status === 'present' && (
            <div className="me-2" style={{ flex: '1' }}>
              <Form.Select
                size="sm"
                className="form-control-sm"
                style={{ width: '90%' }}
                value={nervousSystem?.wasting?.type || ''}
                onChange={(e) => handleChange('wastingType', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="rt ul">Rt UL</option>
                <option value="lt ul">Lt UL</option>
                <option value="rt ll">Rt LL</option>
                <option value="lt ll">Lt LL</option>
              </Form.Select>
            </div>
          )}
          <div style={{ flex: '1' }}>
            <NoteDropdown
              value={nervousSystem?.wasting?.note || ''}
              onChange={(val) => handleChange('wastingNote', val)}
              placeholder="Add note"
              suggestions={allNotes.filter(
                (n) => n.elementType === 'nervousSystem' && n.itemElement === 'wasting',
              )}
            />
          </div>
        </Col>
      </Row>
      {/* Power */}
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Power:
        </Col>

        {/* MRC Grade Input */}
        <Col md={5}>
          <Form.Control
            size="sm"
            type="number"
            placeholder="MRC Grade (0 - 5)"
            value={getValueFromFraction(nervousSystem?.power?.value) || ''}
            onChange={(e) => handleChange('powerValue', e.target.value)}
            style={{ display: 'inline-block', width: '80%' }}
          />
          {/* <Form.Control
            className="form-control-sm"
            type="number"
            placeholder="Enter GCS"
            value={nervousSystem?.gcs?.value || ''} // Updated to use correct path
            onClick={() => setIsBtn(isBtn ? false : true)}
            style={{ width: '80%', display: 'inline-block' }}
            onChange={(e) => handleChange('gcs', null, null, e.target.value)}
          /> */}
          <span
            style={{
              fontSize: '1.1rem',
              marginLeft: '.2rem',
            }}
          >
            / 5
          </span>

          {/* Checkboxes directly below MRC Grade */}
          <Row className="mt-2">
            {powerTypes.map((type, index) => (
              <Col xs={6} key={type} className="mb-1">
                <Form.Check
                  type="checkbox"
                  id={`power-type-${type}`}
                  label={type}
                  checked={(selectedData.power?.type || []).includes(type)}
                  onChange={(e) => handleChange('powerType', type)}
                />
              </Col>
            ))}
          </Row>
        </Col>

        {/* Note Input */}
        <Col md={4} className="d-flex flex-column align-items-start">
          <NoteDropdown
            value={selectedData.power?.note || ''}
            onChange={(val) => handleChange('powerNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'power',
            )}
          />
        </Col>
      </Row>
      {/* Jerks */}
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Jerks:
        </Col>
        <Col md={9}>
          {[
            { label: 'Biceps', key: 'biceps' },
            { label: 'Triceps', key: 'triceps' },
            { label: 'Supinator', key: 'supinator' },
            { label: 'Knee', key: 'knee' },
            { label: 'Ankle', key: 'ankle' },
            { label: 'Jaw', key: 'jaw' },
          ].map(({ label, key }) => {
            const item = nervousSystem?.jerks?.[key] || {};
            const isChecked = !!item.checked;
            return (
              <div
                key={key}
                className="mb-2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: isChecked ? '#f8f9ff' : 'transparent',
                  borderRadius: '6px',
                  padding: isChecked ? '4px 8px' : '2px 8px',
                  border: isChecked
                    ? '1px solid #d0d8f0'
                    : '1px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                <input
                  type="checkbox"
                  id={`jerks_${key}`}
                  name={`jerks_${key}`}
                  checked={isChecked}
                  onChange={() => {}}
                  onClick={() => handleChange('jerksChecked', null, key)}
                  style={{ cursor: 'pointer' }}
                />
                <label
                  htmlFor={`jerks_${key}`}
                  style={{
                    marginLeft: '6px',
                    marginBottom: 0,
                    minWidth: '80px',
                    fontWeight: isChecked ? 600 : 400,
                    color: isChecked ? '#3a4a8a' : '#555',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </label>
                {isChecked && (
                  <>
                    <Form.Select
                      size="sm"
                      className="form-control-sm"
                      style={{ flex: '0 0 180px' }}
                      value={item.status || ''}
                      onChange={(e) =>
                        handleChange('jerksStatus', e.target.value, key)
                      }
                    >
                      <option value="">Select...</option>
                      <option value="normal">Normal</option>
                      <option value="present with reinforcement">
                        Present with reinforcement
                      </option>
                      <option value="reduced">Reduced</option>
                      <option value="absent">Absent</option>
                      <option value="increased/exaggerated">
                        Increased/exaggerated
                      </option>
                      <option value="exaggerated with clonus">
                        Exaggerated with clonus
                      </option>
                    </Form.Select>
                    <NoteDropdown
                      value={item.note || ''}
                      onChange={(val) => handleChange('jerksNote', val, key)}
                      placeholder="Add note"
                      suggestions={allNotes.filter(
                        (n) => n.elementType === 'nervousSystem' && n.itemElement === `jerks_${key}`,
                      )}
                    />
                  </>
                )}
              </div>
            );
          })}
        </Col>
      </Row>
      {/* Planter Response */}
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Reflexes:
        </Col>
        <Col md={9}>
          {[
            { label: "Glabellar (Myerson's sign)", key: 'glabellar' },
            { label: 'Planter RT', key: 'planterRt' },
            { label: 'Planter LT', key: 'planterLt' },
          ].map(({ label, key }) => {
            const item = nervousSystem?.plantorResponse?.[key] || {};
            const isChecked = !!item.checked;
            return (
              <div
                key={key}
                className="mb-2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: isChecked ? '#f8f9ff' : 'transparent',
                  borderRadius: '6px',
                  padding: isChecked ? '4px 8px' : '2px 8px',
                  border: isChecked
                    ? '1px solid #d0d8f0'
                    : '1px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                <input
                  type="radio"
                  id={`reflex_${key}`}
                  name={`reflex_${key}`}
                  checked={isChecked}
                  onChange={() => {}}
                  onClick={() => handleChange('reflexChecked', null, key)}
                  style={{ cursor: 'pointer' }}
                />
                <label
                  htmlFor={`reflex_${key}`}
                  style={{
                    marginLeft: '6px',
                    marginBottom: 0,
                    minWidth: '160px',
                    fontWeight: isChecked ? 600 : 400,
                    color: isChecked ? '#3a4a8a' : '#555',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </label>
                {isChecked && (
                  <>
                    <Form.Select
                      size="sm"
                      className="form-control-sm"
                      style={{ flex: '0 0 180px' }}
                      value={item.status || ''}
                      onChange={(e) =>
                        handleChange('reflexStatus', e.target.value, key)
                      }
                    >
                      <option value="">Select...</option>
                      <option value="flexor">Flexor</option>
                      <option value="extensor">Extensor</option>
                      <option value="absent">Absent</option>
                    </Form.Select>
                    <NoteDropdown
                      value={item.note || ''}
                      onChange={(val) => handleChange('reflexNote', val, key)}
                      placeholder="Add note"
                      suggestions={allNotes.filter(
                        (n) => n.elementType === 'nervousSystem' && n.itemElement === `plantorResponse_${key}`,
                      )}
                    />
                  </>
                )}
              </div>
            );
          })}
        </Col>
      </Row>
      {/* Gait */}
      <Row className="title mt-4">
        <Col>Gait</Col>
      </Row>
      <hr />
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Gait:
        </Col>
        <Col md={5}>
          <Row>
            {[
              { label: 'Normal', value: 'normal' },
              { label: 'Abnormal', value: 'abnormal' },
            ].map((option, index) => (
              <Col md={6} className="mb-2" key={index}>
                <Form.Check
                  type="radio"
                  id={`gait_${option.value}`}
                  label={option.label}
                  name="gaitFunction"
                  checked={nervousSystem?.gaitFunction?.status === option.value}
                  onClick={() =>
                    handleChange(
                      'gaitFunctionStatus',
                      nervousSystem?.gaitFunction?.status === option.value
                        ? ''
                        : option.value,
                    )
                  }
                />
              </Col>
            ))}
          </Row>
        </Col>
        <Col
          md={4}
          className={`d-flex align-items-flex-start ${
            nervousSystem?.gaitFunction?.status ? 'gap-2' : ''
          }`}
        >
          {nervousSystem?.gaitFunction?.status === 'abnormal' && (
            <div className="me-2" style={{ flex: '1' }}>
              <Form.Select
                size="sm"
                className="form-control-sm"
                style={{ width: '90%' }}
                value={nervousSystem?.gaitFunction?.type || ''}
                onChange={(e) =>
                  handleChange('gaitFunctionType', e.target.value)
                }
              >
                <option value="">Select...</option>
                <option value="Antalgic gait">Antalgic gait</option>
                <option value="Hemiplegic gait">Hemiplegic gait</option>
                <option value="Scissar gait">Scissar gait</option>
                <option value="Waddling gait">Waddling gait</option>
                <option value="High Steppage gait">High Steppage gait</option>
                <option value="Afaxic gait">Afaxic gait</option>
                <option value="Festinant gait">Festinant gait</option>
                <option value="Spastic gait">Spastic gait</option>
              </Form.Select>
            </div>
          )}
          <div
            style={{ flex: nervousSystem?.gaitFunction?.status ? '1' : '1' }}
          >
            <NoteDropdown
              value={nervousSystem?.gaitFunction?.note || ''}
              onChange={(val) => handleChange('gaitFunctionNote', val)}
              placeholder="Add note"
              suggestions={allNotes.filter(
                (n) => n.elementType === 'nervousSystem' && n.itemElement === 'gaitFunction',
              )}
            />
          </div>
        </Col>
      </Row>
      {/* SLR Test */}
      <Row className="title mt-4">
        <Col>SLR Test</Col>
      </Row>
      <hr />
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          SLR Test:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Control
                size="sm"
                type="number"
                placeholder="Enter RT"
                value={nervousSystem?.slrTest?.rt || ''}
                onChange={(e) => {
                  const updatedData = { ...selectedData };
                  handleOnExaminationData(
                    {
                      ...updatedData,
                      slrTest: {
                        ...nervousSystem?.slrTest,
                        rt: e.target.value,
                      },
                    },
                    'nervousSystem',
                  );
                }}
              />
            </Col>
            <Col md={6}>
              <Form.Control
                size="sm"
                type="number"
                placeholder="Enter LT"
                value={nervousSystem?.slrTest?.lt || ''}
                onChange={(e) => {
                  const updatedData = { ...selectedData };
                  handleOnExaminationData(
                    {
                      ...updatedData,
                      slrTest: {
                        ...nervousSystem?.slrTest,
                        lt: e.target.value,
                      },
                    },
                    'nervousSystem',
                  );
                }}
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.gaitFunction?.note || ''}
            onChange={(val) => handleChange('gaitFunctionNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'gaitFunction',
            )}
          />
        </Col>
      </Row>
      {/* Cerebellar Function */}
      <Row className="title mt-4">
        <Col>Cerebellar Function</Col>
      </Row>
      <hr />
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Cerebellar Function:
        </Col>
        <Col md={5}>
          <Row>
            {[
              { label: 'Intact', value: 'intact' },
              { label: 'Impaired', value: 'impaired' },
            ].map((option, index) => (
              <Col md={6} className="mb-2" key={index}>
                <Form.Check
                  type="radio"
                  id={`cerebellar_${option.value}`}
                  label={option.label}
                  name="cerebellarFunction"
                  checked={
                    nervousSystem?.cerebellarFunction?.status === option.value
                  }
                  onClick={() =>
                    handleChange(
                      'cerebellarFunctionStatus',
                      nervousSystem?.cerebellarFunction?.status === option.value
                        ? ''
                        : option.value,
                    )
                  }
                />
              </Col>
            ))}
          </Row>
        </Col>
        <Col
          md={4}
          className={`d-flex align-items-flex-start ${
            nervousSystem?.cerebellarFunction?.status ? 'gap-2' : ''
          }`}
        >
          {nervousSystem?.cerebellarFunction?.status && (
            <div className="me-2" style={{ flex: '1' }}>
              <Form.Select
                size="sm"
                className="form-control-sm"
                style={{ width: '90%' }}
                value={nervousSystem?.cerebellarFunction?.type || ''}
                onChange={(e) =>
                  handleChange('cerebellarFunctionType', e.target.value)
                }
              >
                <option value="">Select...</option>
                <option value="Rt">Rt</option>
                <option value="Lt">Lt</option>
              </Form.Select>
            </div>
          )}
          <div
            style={{
              flex: nervousSystem?.cerebellarFunction?.status ? '1' : '1',
            }}
          >
            <NoteDropdown
              value={nervousSystem?.cerebellarFunction?.note || ''}
              onChange={(val) => handleChange('cerebellarFunctionNote', val)}
              placeholder="Add note"
              suggestions={allNotes.filter(
                (n) => n.elementType === 'nervousSystem' && n.itemElement === 'cerebellarFunction',
              )}
            />
          </div>
        </Col>
      </Row>
      {/* Sensory System */}
      <Row className="title mt-4">
        <Col>Sensory System</Col>
      </Row>
      <hr />
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Sensory System:
        </Col>
        <Col md={5}>
          <Row>
            {[
              {
                label: 'Pain Sensation',
                value: 'pain sensation',
              },
              {
                label: 'Touch Sensation',
                value: 'touch sensation',
              },
              { label: 'Smell Sensation', value: 'smell sensation' },
              { label: 'Sense of Vibration', value: 'sense of vibration' },
            ].map((option, index) => (
              <Col md={12} className="mb-2" key={index}>
                <Form.Check
                  type="radio"
                  id={`sensory_${option.value}`}
                  label={option.label}
                  name="sensorySystem"
                  checked={
                    nervousSystem?.sensorySystem?.status === option.value
                  }
                  onClick={() =>
                    handleChange(
                      'sensorySystemStatus',
                      nervousSystem?.sensorySystem?.status === option.value
                        ? ''
                        : option.value,
                    )
                  }
                />
              </Col>
            ))}
          </Row>
        </Col>
        <Col
          md={4}
          className={`d-flex flex-column ${
            nervousSystem?.sensorySystem?.status ? 'gap-2' : ''
          }`}
        >
          {/* Dropdown for options */}
          {nervousSystem?.sensorySystem?.status && (
            <Form.Select
              size="sm"
              className="form-control-sm"
              style={{ width: '100%' }}
              value={nervousSystem?.sensorySystem?.type || ''}
              onChange={(e) =>
                handleChange('sensorySystemType', e.target.value)
              }
            >
              <option value="">Select...</option>
              {/* Conditional dropdown options */}
              {nervousSystem.sensorySystem.status === 'rombergism' ? (
                <>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                </>
              ) : (
                <>
                  <option value="rt upper limb - Intact">
                    Rt upper limb - Intact
                  </option>
                  <option value="rt upper limb - Impaired">
                    Rt upper limb - Impaired
                  </option>
                  <option value="rt upper limb - Absent">
                    Rt upper limb - Absent
                  </option>
                  <option value="lt upper limb - Intact">
                    Lt upper limb - Intact
                  </option>
                  <option value="lt upper limb - Impaired">
                    Lt upper limb - Impaired
                  </option>
                  <option value="lt upper limb - Absent">
                    Lt upper limb - Absent
                  </option>
                </>
              )}
            </Form.Select>
          )}
          {/* Textarea for notes */}
          <NoteDropdown
            value={nervousSystem?.sensorySystem?.note || ''}
            onChange={(val) => handleChange('sensorySystemNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'sensorySystem',
            )}
          />
        </Col>
      </Row>
      <Row className="title mt-3">
        <Col>Signs of Meniaged Imitation</Col>
      </Row>
      <hr />
      {/* Neck Rigidity */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          Neck Rigidity:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="neck_rigidity_present"
                name="neckRigidity"
                label="Present"
                checked={nervousSystem?.neckRigidity?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'neckRigidityStatus',
                    nervousSystem?.neckRigidity?.status === 'present'
                      ? ''
                      : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="neck_rigidity_absent"
                name="neckRigidity"
                label="Absent"
                checked={nervousSystem?.neckRigidity?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'neckRigidityStatus',
                    nervousSystem?.neckRigidity?.status === 'absent'
                      ? ''
                      : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.neckRigidity?.note || ''}
            onChange={(val) => handleChange('neckRigidityNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'neckRigidity',
            )}
          />
        </Col>
      </Row>
      {/* Kernig Sign */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          Kernig's Sign:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="kernig_sign_present"
                name="kernigSign"
                label="Present"
                checked={nervousSystem?.kernigSign?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'kernigSignStatus',
                    nervousSystem?.kernigSign?.status === 'present'
                      ? ''
                      : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="kernig_sign_absent"
                name="kernigSign"
                label="Absent"
                checked={nervousSystem?.kernigSign?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'kernigSignStatus',
                    nervousSystem?.kernigSign?.status === 'absent'
                      ? ''
                      : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.kernigSign?.note || ''}
            onChange={(val) => handleChange('kernigSignNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'kernigSign',
            )}
          />
        </Col>
      </Row>
      {/* Brudzinski Sign */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          Brudzinski Sign:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="brudzinski_sign_present"
                name="brudzinskiSign"
                label="Present"
                checked={nervousSystem?.brudzinskiSign?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'brudzinskiSignStatus',
                    nervousSystem?.brudzinskiSign?.status === 'present'
                      ? ''
                      : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="brudzinski_sign_absent"
                name="brudzinskiSign"
                label="Absent"
                checked={nervousSystem?.brudzinskiSign?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'brudzinskiSignStatus',
                    nervousSystem?.brudzinskiSign?.status === 'absent'
                      ? ''
                      : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.brudzinskiSign?.note || ''}
            onChange={(val) => handleChange('brudzinskiSignNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'brudzinskiSign',
            )}
          />
        </Col>
      </Row>
      <Row className="title mt-3">
        <Col>Cardiovascular System</Col>
      </Row>
      <hr />
      {/* JVP */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          JVP:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="jvp_normal"
                name="jvp"
                label="Normal"
                checked={nervousSystem?.jvp?.status === 'normal'}
                onClick={() =>
                  handleChange(
                    'jvpStatus',
                    nervousSystem?.jvp?.status === 'normal' ? '' : 'normal',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="jvp_raised"
                name="jvp"
                label="Raised"
                checked={nervousSystem?.jvp.status === 'raised'}
                onClick={() =>
                  handleChange(
                    'jvpStatus',
                    nervousSystem?.jvp?.status === 'raised' ? '' : 'raised',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.jvp?.note || ''}
            onChange={(val) => handleChange('jvpNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'jvp',
            )}
          />
        </Col>
      </Row>
      {/* Pulse */}
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Pulse:
        </Col>
        <Col md={5}>
          <Row className="align-items-flex-start">
            <Col md={6}>
              <Form.Check
                type="radio"
                id="pulse_regular"
                label="Regular"
                name="pulse"
                checked={nervousSystem?.pulse?.status === 'regular'}
                onClick={() =>
                  handleChange(
                    'pulseStatus',
                    nervousSystem?.pulse?.status === 'regular' ? '' : 'regular',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="pulse_irregular"
                label="Irregular"
                name="pulse"
                checked={nervousSystem?.pulse?.status === 'irregular'}
                onClick={() =>
                  handleChange(
                    'pulseStatus',
                    nervousSystem?.pulse?.status === 'irregular'
                      ? ''
                      : 'irregular',
                  )
                }
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Check
                type="radio"
                id="pulse_drop_beat"
                label="Drop Beat"
                name="pulse"
                checked={nervousSystem?.pulse?.status === 'drop beat'}
                onClick={() =>
                  handleChange(
                    'pulseStatus',
                    nervousSystem?.pulse?.status === 'drop beat'
                      ? ''
                      : 'drop beat',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="pulse_absent"
                label="Absent"
                name="pulse"
                checked={nervousSystem?.pulse?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'pulseStatus',
                    nervousSystem?.pulse?.status === 'absent' ? '' : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.pulse?.note || ''}
            onChange={(val) => handleChange('pulseNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'pulse',
            )}
          />
        </Col>
      </Row>
      {/* Murmur */}
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Murmur:
        </Col>
        <Col md={5}>
          <Row className="align-items-flex-start">
            <Col md={6}>
              <Form.Check
                type="radio"
                id="murmur_present"
                label="Present"
                name="murmur"
                checked={nervousSystem?.murmur?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'murmurStatus',
                    nervousSystem?.murmur?.status === 'present'
                      ? ''
                      : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="murmur_absent"
                label="Absent"
                name="murmur"
                checked={nervousSystem?.murmur?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'murmurStatus',
                    nervousSystem?.murmur?.status === 'absent' ? '' : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.murmur?.note || ''}
            onChange={(val) => handleChange('murmurNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'murmur',
            )}
          />
        </Col>
      </Row>
      <Row className="title mt-3">
        <Col>Gastrointestinal System</Col>
      </Row>
      <hr />
      {/* Ascitis */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          Ascitis:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="ascitis_present"
                name="ascitis"
                label="Present"
                checked={nervousSystem?.ascitis?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'ascitisStatus',
                    nervousSystem?.ascitis?.status === 'present'
                      ? ''
                      : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="ascitis_absent"
                name="ascitis"
                label="Absent"
                checked={nervousSystem?.ascitis?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'ascitisStatus',
                    nervousSystem?.ascitis?.status === 'absent' ? '' : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.ascitis?.note || ''}
            onChange={(val) => handleChange('ascitisNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'ascitis',
            )}
          />
        </Col>
      </Row>
      {/* Hepatomegaly */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          Hepatomegaly:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="hepatomegaly_present"
                name="hepatomegaly"
                label="Present"
                checked={nervousSystem?.hepatomegaly?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'hepatomegalyStatus',
                    nervousSystem?.hepatomegaly?.status === 'present'
                      ? ''
                      : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="hepatomegaly_absent"
                name="hepatomegaly"
                label="Absent"
                checked={nervousSystem?.hepatomegaly?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'hepatomegalyStatus',
                    nervousSystem?.hepatomegaly?.status === 'absent'
                      ? ''
                      : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.hepatomegaly?.note || ''}
            onChange={(val) => handleChange('hepatomegalyNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'hepatomegaly',
            )}
          />
        </Col>
      </Row>
      {/* Splenomegaly */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          Splenomegaly:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="splenomegaly_present"
                name="splenomegaly"
                label="Present"
                checked={nervousSystem?.splenomegaly?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'splenomegalyStatus',
                    nervousSystem?.splenomegaly?.status === 'present'
                      ? ''
                      : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="splenomegaly_absent"
                name="splenomegaly"
                label="Absent"
                checked={nervousSystem?.splenomegaly?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'splenomegalyStatus',
                    nervousSystem?.splenomegaly?.status === 'absent'
                      ? ''
                      : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.splenomegaly?.note || ''}
            onChange={(val) => handleChange('splenomegalyNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'splenomegaly',
            )}
          />
        </Col>
      </Row>
      <Row className="title mt-3">
        <Col>Respiratory System</Col>
      </Row>
      <hr />
      {/* RR */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          RR:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="rr_minimum"
                name="rr"
                label="Minimum"
                checked={nervousSystem?.rr?.status === 'minimum'}
                //onChange={() => handleChange('rrStatus', 'minimum')}
                onClick={() =>
                  handleChange(
                    'rrStatus',
                    nervousSystem?.rr?.status === 'minimum' ? '' : 'minimum',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="rr_regular"
                name="rr"
                label="Regular"
                checked={nervousSystem?.rr?.status === 'regular'}
                //onChange={() => handleChange('rrStatus', 'regular')}
                onClick={() =>
                  handleChange(
                    'rrStatus',
                    nervousSystem?.rr?.status === 'regular' ? '' : 'regular',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.rr?.note || ''}
            onChange={(val) => handleChange('rrNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'rr',
            )}
          />
        </Col>
      </Row>
      {/* Rhanchi */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          Rhanchi:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="rhanchi_present"
                name="rhanchi"
                label="Present"
                checked={nervousSystem?.rhanchi?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'rhanchiStatus',
                    nervousSystem?.rhanchi?.status === 'present'
                      ? ''
                      : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="rhanchi_absent"
                name="rhanchi"
                label="Absent"
                checked={nervousSystem?.rhanchi?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'rhanchiStatus',
                    nervousSystem?.rhanchi?.status === 'absent' ? '' : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.rhanchi?.note || ''}
            onChange={(val) => handleChange('rhanchiNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'rhanchi',
            )}
          />
        </Col>
      </Row>
      {/* Creps */}
      <Row className="mb-3 align-items-center">
        <Col md={3} className="label-text">
          Creps:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="creps_present"
                name="creps"
                label="Present"
                checked={nervousSystem?.creps?.status === 'present'}
                onClick={() =>
                  handleChange(
                    'crepsStatus',
                    nervousSystem?.creps?.status === 'present' ? '' : 'present',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="creps_absent"
                name="creps"
                label="Absent"
                checked={nervousSystem?.creps?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'crepsStatus',
                    nervousSystem?.creps?.status === 'absent' ? '' : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.creps?.note || ''}
            onChange={(val) => handleChange('crepsNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'creps',
            )}
          />
        </Col>
      </Row>
      {/* Breath Sound */}
      <Row className="mb-3 align-items-flex-start">
        <Col md={3} className="label-text">
          Breath Sound:
        </Col>
        <Col md={5}>
          <Row>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="breath_sound_normal"
                name="breathSound"
                label="Normal"
                checked={nervousSystem?.breathSound?.status === 'normal'}
                onClick={() =>
                  handleChange(
                    'breathSoundStatus',
                    nervousSystem?.breathSound?.status === 'normal'
                      ? ''
                      : 'normal',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="breath_sound_bronchial"
                name="breathSound"
                label="Bronchial"
                checked={nervousSystem?.breathSound?.status === 'bronchial'}
                onClick={() =>
                  handleChange(
                    'breathSoundStatus',
                    nervousSystem?.breathSound?.status === 'bronchial'
                      ? ''
                      : 'bronchial',
                  )
                }
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Check
                type="radio"
                id="breath_sound_reduced"
                name="breathSound"
                label="Reduced"
                checked={nervousSystem?.breathSound?.status === 'reduced'}
                onClick={() =>
                  handleChange(
                    'breathSoundStatus',
                    nervousSystem?.breathSound?.status === 'reduced'
                      ? ''
                      : 'reduced',
                  )
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="radio"
                id="breath_sound_absent"
                name="breathSound"
                label="Absent"
                checked={nervousSystem?.breathSound?.status === 'absent'}
                onClick={() =>
                  handleChange(
                    'breathSoundStatus',
                    nervousSystem?.breathSound?.status === 'absent'
                      ? ''
                      : 'absent',
                  )
                }
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <NoteDropdown
            value={nervousSystem?.breathSound?.note || ''}
            onChange={(val) => handleChange('breathSoundNote', val)}
            placeholder="Add note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'nervousSystem' && n.itemElement === 'breathSound',
            )}
          />
        </Col>
      </Row>
    </>
  );
};

export default NeurologyExamination;
